import type { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import type { SeededRandom } from "../core/SeededRandom";

const LOCAL_HORIZONTAL = new Vector3(1, 0, 0);

export function createHitscanRays(
  origin: Vector3,
  forward: Vector3,
  count: number,
  spreadRadians: number,
  range: number,
  random: SeededRandom,
): Ray[] {
  const rays: Ray[] = [];
  const right = Vector3.Cross(Vector3.Up(), forward).normalize();
  const up = Vector3.Cross(forward, right).normalize();
  for (let index = 0; index < count; index += 1) {
    const angle = random.range(0, Math.PI * 2);
    const radius = Math.sqrt(random.next()) * Math.tan(spreadRadians);
    const direction = forward
      .add(right.scale(Math.cos(angle) * radius))
      .add(up.scale(Math.sin(angle) * radius))
      .normalize();
    rays.push(new Ray(origin.clone(), direction, range));
  }
  return rays;
}

export function pickHitscan(scene: Scene, ray: Ray): PickingInfo | null {
  return scene.pickWithRay(
    ray,
    (mesh) => mesh.isPickable && mesh.isEnabled() && mesh.visibility > 0,
    false,
  );
}

export function findVerticalAutoAimDirection(
  scene: Scene,
  origin: Vector3,
  forward: Vector3,
  range: number,
): Vector3 | undefined {
  const horizontalForward = new Vector3(forward.x, 0, forward.z);
  if (horizontalForward.lengthSquared() <= 0.000001) return undefined;
  horizontalForward.normalize();

  const candidates = scene.meshes
    .filter((mesh) => {
      const enemyId = (mesh.metadata as { enemyId?: unknown } | null)?.enemyId;
      if (typeof enemyId !== "string" || !mesh.isPickable || !mesh.isEnabled()) return false;
      if (!mesh.isVisible || mesh.visibility <= 0) return false;

      mesh.computeWorldMatrix(true);
      const target = mesh.getAbsolutePosition();
      const offsetX = target.x - origin.x;
      const offsetZ = target.z - origin.z;
      const forwardDistance =
        offsetX * horizontalForward.x + offsetZ * horizontalForward.z;
      if (forwardDistance <= 0 || forwardDistance > range) return false;

      const lateralDistance = Math.abs(
        offsetX * horizontalForward.z - offsetZ * horizontalForward.x,
      );
      const localHalfWidth = mesh.getBoundingInfo().boundingBox.extendSize.x;
      const worldHalfWidthVector = Vector3.TransformNormal(
        LOCAL_HORIZONTAL.scale(localHalfWidth),
        mesh.getWorldMatrix(),
      );
      const worldHalfWidth = Math.hypot(worldHalfWidthVector.x, worldHalfWidthVector.z);
      return lateralDistance <= worldHalfWidth;
    })
    .sort(
      (left, right) =>
        Vector3.DistanceSquared(origin, left.getAbsolutePosition()) -
        Vector3.DistanceSquared(origin, right.getAbsolutePosition()),
    );

  for (const candidate of candidates) {
    const target = candidate.getAbsolutePosition();
    const forwardDistance =
      (target.x - origin.x) * horizontalForward.x +
      (target.z - origin.z) * horizontalForward.z;
    const verticalSlope = (target.y - origin.y) / forwardDistance;
    const direction = new Vector3(
      horizontalForward.x,
      verticalSlope,
      horizontalForward.z,
    ).normalize();
    const hit = pickHitscan(scene, new Ray(origin.clone(), direction, range));
    const candidateId = (candidate.metadata as { enemyId?: string } | null)?.enemyId;
    const hitId = (hit?.pickedMesh?.metadata as { enemyId?: string } | null)?.enemyId;
    if (hit?.hit && candidateId && hitId === candidateId) return direction;
  }
  return undefined;
}

export function createAutoAimedHitscanRays(
  origin: Vector3,
  forward: Vector3,
  autoAimDirection: Vector3,
  count: number,
  spreadRadians: number,
  range: number,
  random: SeededRandom,
): Ray[] {
  const rays = createHitscanRays(origin, forward, count, spreadRadians, range, random);
  if (rays.length > 0) rays[0] = new Ray(origin.clone(), autoAimDirection.normalize(), range);
  return rays;
}
